import { useEffect, useMemo, useState } from "react";
import HeaderSection from "../../components/HeaderSection";
import TimePane from "../../components/TimePane";
import Line from "../../components/Line";
import ClaimHeader from "../../components/ClaimHeader";
import ClaimComponent from "../../components/ClaimComponent";
import { Button } from "@material-tailwind/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { mapAssetForUi } from "../../utils/icons";
import { useWallet } from "../../context/WalletContext";

export default function ClaimPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const willId = params.get('id');
    const { wallet, isConnected, connectDemo, demoWallets } = useWallet();
    const [will, setWill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [claiming, setClaiming] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        async function load() {
            if (!willId) {
                setError('Missing will id');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError('');
            try {
                const data = await api.getWill(willId);
                if (!cancelled) setWill(data);
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [willId]);

    const assets = useMemo(
        () => (will?.assets || []).map(mapAssetForUi),
        [will]
    );

    const canClaim = will?.status === 'to_claim';

    const handleClaim = async () => {
        setMessage('');
        setError('');

        let activeWallet = wallet;
        if (!activeWallet) {
            const heir = demoWallets.find((item) => item.label.toLowerCase().includes('heir'));
            if (heir) {
                connectDemo(heir.address);
                activeWallet = heir.address;
            }
        }

        if (!activeWallet) {
            setError('Connect the heir wallet to claim');
            return;
        }

        setClaiming(true);
        try {
            const data = await api.claimWill(willId, activeWallet);
            setWill(data);
            setMessage('Claim successful. Assets are now marked as claimed.');
        } catch (err) {
            setError(err.message);
        } finally {
            setClaiming(false);
        }
    };

    return (
        <div className="bg-[#121212] min-w-0">
            <HeaderSection account={false} />
            <TimePane
                availableAt={will?.available_at}
                canClaim={canClaim}
                claiming={claiming}
                onClaim={handleClaim}
            />
            {(message || error || (!isConnected && canClaim)) && (
                <div className="px-4 sm:px-[20px] lg:px-[150px] pb-4">
                    {message && <p className="text-[#FBBF04]">{message}</p>}
                    {error && <p className="text-red-400">{error}</p>}
                    {!isConnected && canClaim && !error && (
                        <p className="text-gray1 text-sm">Tip: connect the Heir wallet before claiming.</p>
                    )}
                </div>
            )}
            <div className="w-full min-w-0">
                <div className="flex flex-col px-4 sm:px-[20px] lg:px-[150px]">
                    <Line title='All Assets to claim' />
                </div>
                <ClaimHeader />
                {loading && <div className="px-4 sm:px-[20px] lg:px-[150px] text-gray1 py-8">Loading claim...</div>}
                {!loading && !error && assets.map((item) => (
                    <div key={item.id}>
                        <ClaimComponent item={item} />
                    </div>
                ))}
                <div className="flex flex-col px-4 sm:px-[20px] lg:px-[150px] mt-10 sm:mt-[120px]">
                    <Line title='Note of the will' />
                    <div className="bg-bg w-full min-h-[160px] mt-6 sm:mt-[50px] p-6 text-white whitespace-pre-wrap">
                        {will?.note || 'No note for this will.'}
                    </div>

                    <div className="flex my-[40px]" onClick={() => navigate('/')}>
                        <Button className="py-[10px] px-[48px] border border-[#FBBF04] rounded-[10px] text-[#FBBF04] text-[13px] font-semibold">Back</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
